import React, { Component } from 'react';
import SwipeableViews from 'react-swipeable-views';
import { withRouter } from 'react-router-dom';
import SEO from "./components/SEO";
import books from './data/books';
import Devotionals from './plans/Devotionals';
import { Container, HeaderText } from './components/Core/TextComponents';
import TitleLine from './components/Core/TitleLine';
import { ThemeContext } from './components/Core/ThemeContext';
import ScrollArea from './components/Core/ScrollArea';
import BibleReader from './components/BibleReader';
var bcv_parser = require("bible-passage-reference-parser/js/en_bcv_parser").bcv_parser;
var bcv = new bcv_parser;

const querystring = require('querystring');
import { withAlert } from 'react-alert';

import {
     Tabs, Tab,
} from 'react-bootstrap-legacy';

import { Capacitor } from '@capacitor/core';
import { trackCustomEvent } from './utilities/analytics';
import { SubheaderText, CommentText, CommentHeader, CommentNotes, SmallGutter, LargeGutter } from './components/Core/TextComponents';
import PlanOutline from './components/Outlines/PlanOutline';
import VerseOutline from './components/Outlines/VerseOutline';
import osisConverters from './utilities/osisConversions';
import { buildCanonicalUrl } from './utilities/canonicalUrls';

const web_version = (Capacitor.getPlatform()==='web');

const normalizeSnippetText = (value = '') => `${value}`
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim();

const truncateSnippet = (value, maxLength = 160) => {
    const normalized = normalizeSnippetText(value);
    if (normalized.length <= maxLength) return normalized;
    return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
};

const trimTrailingPunctuation = (value = '') => `${value}`.replace(/[.,;:!?]+$/g, '').trim();

const collectUniqueNames = (verses = [], key) => {
    const seen = new Set();
    const names = [];

    for (const verse of verses) {
        const group = verse?.[key]?.known || [];
        for (const item of group) {
            if (!item?.name || seen.has(item.name)) continue;
            seen.add(item.name);
            names.push(item.name);
            if (names.length >= 3) return names;
        }
    }

    return names;
};

const buildChapterIntro = (response = {}, searchedVerse = '') => {
    const verses = response.verses || [];
    const book = response.book || {};
    const within = response.literarycontext?.within || [];
    const firstHeading = within[0]?.heading || verses[0]?.sentence?.heading || '';
    const secondHeading = within[1]?.heading || '';
    const people = collectUniqueNames(verses, 'who');
    const places = collectUniqueNames(verses, 'where');

    const parts = [];
    if (book.Name && searchedVerse) {
        parts.push(`${book.Name} ${searchedVerse.split('.')[1]} belongs to the ${book.Subgroup || book.Group || 'Bible'} and focuses on ${firstHeading || 'this passage'}.`);
    } else if (firstHeading) {
        parts.push(`This chapter focuses on ${firstHeading}.`);
    }

    if (book.Summary) {
        parts.push(book.Summary);
    }
    if (secondHeading && secondHeading !== firstHeading) {
        parts.push(`It also includes ${secondHeading}.`);
    }
    if (people.length > 0) {
        parts.push(`Key people include ${people.join(', ')}.`);
    }
    if (places.length > 0) {
        parts.push(`Key places include ${places.join(', ')}.`);
    }

    return normalizeSnippetText(parts.join(' '));
};

const buildVerseSeoTitle = ({ readingTitle, searchedVerse, devotionalTitle }) => {
    const reference = readingTitle || searchedVerse || 'Bible Reading';
    if (devotionalTitle) {
        return `${devotionalTitle}: Read ${reference} | ai-BIBLE`;
    }
    return `${reference} | Bible Reading, Context & Cross References | ai-BIBLE`;
};

const buildVerseSeoDescription = ({
    readingTitle,
    searchedVerse,
    devotionalTitle,
    commentary,
    verseText,
    chapterIntro,
    subgroup,
}) => {
    const reference = readingTitle || searchedVerse || 'this passage';
    const normalizedCommentary = normalizeSnippetText(commentary);
    const normalizedVerseText = normalizeSnippetText(verseText);
    const normalizedChapterIntro = normalizeSnippetText(chapterIntro);
    const normalizedSubgroup = normalizeSnippetText(subgroup);
    const verseExcerpt = truncateSnippet(normalizedVerseText, 88);
    const quotedVerseExcerpt = verseExcerpt ? `"${trimTrailingPunctuation(verseExcerpt)}"` : '';
    const labelPrefix = normalizedSubgroup
        ? `${normalizedSubgroup}: ${reference}`
        : `${reference}`;

    if (devotionalTitle && normalizedCommentary) {
        return truncateSnippet(
            `Read ${reference} with verse text, reflection, and guidance from ${devotionalTitle}. "${verseExcerpt}" ${normalizedCommentary}`,
        );
    }

    if (quotedVerseExcerpt && normalizedChapterIntro) {
        return truncateSnippet(
            `${labelPrefix}: ${quotedVerseExcerpt} Read with extra context and cross references on ai-BIBLE. ${normalizedChapterIntro}`,
        );
    }

    if (quotedVerseExcerpt && normalizedCommentary) {
        return truncateSnippet(
            `${labelPrefix}: ${quotedVerseExcerpt} Read with extra context and cross references on ai-BIBLE. ${normalizedCommentary}`,
        );
    }

    if (quotedVerseExcerpt) {
        return truncateSnippet(
            `${labelPrefix}: ${quotedVerseExcerpt} Read with extra context and cross references on ai-BIBLE.`,
        );
    }

    return truncateSnippet(`Read ${reference} with verse text, context, and related Bible references on ai-BIBLE.`);
};

const decodeQueryValue = (value) => {
    if (value === null || value === undefined) return null;

    try {
        return decodeURIComponent(value);
    } catch (error) {
        return value;
    }
};

const parseJsonQueryValue = (value, fallback = null) => {
    const decoded = decodeQueryValue(value);
    if (decoded === null || decoded === 'null' || decoded === '') return fallback;

    try {
        return JSON.parse(decoded);
    } catch (error) {
        return fallback;
    }
};

class BibleReading extends Component {
    static contextType = ThemeContext;

    static xDown = null;

    static yDown = null;

    querySearchParameters = "";

    bodyScrollLockTargetRef = React.createRef();
    bodyScrollLockTargetElement = null;

    sourcePermissionsRef = React.createRef();

    static getTouches(evt) {
        return evt.touches // browser API
               || evt.originalEvent.touches; // jQuery
    }

    static handleTouchStart(evt) {
        console.log('ts');
        const firstTouch = BibleReader.getTouches(evt)[0];
        BibleReader.xDown = firstTouch.clientX;
        BibleReader.yDown = firstTouch.clientY;
    }

    static handleTouchMove(evt) {
        if (!BibleReader.xDown || !BibleReader.yDown) {
            return;
        }

        const xUp = evt.touches[0].clientX;
        const yUp = evt.touches[0].clientY;

        const xDiff = BibleReader.xDown - xUp;
        const yDiff = BibleReader.yDown - yUp;

        if (Math.abs(xDiff) > Math.abs(yDiff)) { /* most significant */
            if (xDiff > 50) {
                /* left swipe */
                this.incrementPage('');
            } else if (xDiff < -50) {
                /* right swipe */
                this.decrementPage('');
            }
        } else if (yDiff > 0) {
            /* up swipe */
        } else {
            /* down swipe */
        }
        /* reset values */
        BibleReader.xDown = null;
        BibleReader.yDown = null;
    }

    static incrementPage() {
        BibleReading.movePage(1);
    }

    static expandVerse() {
        const elements = window.location.href.split('.');
        
        let url = `${elements[0]}.${elements[1]}`;

        if (window.location.href.includes('ai-bible.com'))
        {
            url = `${elements[0]}.${elements[1]}.${elements[2]}.${elements[3]}`;
        }

        window.location.href = url;
    }

    static decrementPage() {
        BibleReading.movePage(-1);
    }

    static movePage(amount) {
        const path = window.location.pathname.replace(/\/+$/, '');
        const pathMatch = path.match(/^(.*\/read\/[^/]*\.)(\d+)$/);
        const params = new URLSearchParams(window.location.search);
        const verse = params.get('verse');
        const queryMatch = verse && verse.match(/^(.*\.)(\d+)$/);
        const reference = pathMatch
            ? path.split('/read/').pop()
            : queryMatch
                ? verse
                : null;

        if (reference) {
            const referenceMatch = reference.match(/^(.*)\.(\d+)$/);
            const bookCode = referenceMatch && referenceMatch[1];
            const chapter = referenceMatch && parseInt(referenceMatch[2], 10);
            const bookKeys = Object.keys(books.booksData.data);
            const bookIndex = bookKeys.indexOf(bookCode);
            const bookData = books.booksData.data[bookCode];

            if (bookData && bookIndex !== -1 && chapter) {
                const chapterNumbers = bookData.sections.flatMap((section) =>
                    section.osisRange.split('-').map((range) => {
                        const match = range.match(/\.(\d+)(?:\.|$)/);
                        return match ? parseInt(match[1], 10) : 0;
                    })
                );
                const lastChapter = Math.max(...chapterNumbers);
                let targetBookCode = bookCode;
                let targetChapter = chapter + amount;

                if (targetChapter > lastChapter) {
                    if (bookIndex >= bookKeys.length - 1) return;
                    targetBookCode = bookKeys[bookIndex + 1];
                    window.location.href = `/book/${targetBookCode}`;
                    return;
                } else if (targetChapter < 1) {
                    if (bookIndex <= 0) return;
                    targetBookCode = bookKeys[bookIndex - 1];
                    window.location.href = `/book/${targetBookCode}`;
                    return;
                }

                if (pathMatch) {
                    window.location.href = `/read/${targetBookCode}.${targetChapter}/`;
                } else {
                    params.set('verse', `${targetBookCode}.${targetChapter}`);
                    window.location.href = window.web_version
                        ? window.getVerseURL(`${targetBookCode}.${targetChapter}`)
                        : `/reader?${params.toString()}`;
                }
                return;
            }
        }

        if (pathMatch) {
            const page = parseInt(pathMatch[2], 10) + amount;
            window.location.href = `${pathMatch[1]}${page}/`;
            return;
        }

        if (queryMatch) {
            const page = parseInt(queryMatch[2], 10) + amount;
            params.set('verse', `${queryMatch[1]}${page}`);
            window.location.href = window.web_version
                ? window.getVerseURL(`${queryMatch[1]}${page}`)
                : `/reader?${params.toString()}`;
        }
    }

    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

   

    constructor() {
        super();

        console.log('PageBibleReading Constructed')
        // window.scrollTo(0,0)

        window.addEventListener('keydown', (ev) => {
            if (ev.key === 'ArrowRight') BibleReading.incrementPage(ev);
            if (ev.key === 'ArrowLeft') BibleReading.decrementPage(ev);
        });

        window.addEventListener('touchstart', (evt) => { BibleReader.handleTouchStart(evt); }, false);
        window.addEventListener('touchmove', (evt) => { BibleReader.handleTouchMove(evt); }, false);

        this.state = {
            title: '',
            book: '',
            bookMeta: {},
            text: [],
            loading: false,
            sources: {},
            colorIndex: 0,
            chapterIntro: '',
            network: true
        };

        // Network.addListener('networkStatusChange', status => {
        //     console.log('network status change: ', status.connected)
        //     this.setState({network: status.connected})
        // });
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
        //enableBodyScroll(this.bodyScrollLockTargetElement);
        document.body.style.overflow = "";
    }

    handleResize = () => {
        console.log('window ' + window.innerWidth);
        this.forceUpdate();
    };

    componentDidUpdate() {
        window.scrollTo(0, 0);
    }

    setQuerySearchParams() {
        if (window.web_version)
        {
            this.querySearchParameters = window.location.search;
        }
        else
        {
            this.querySearchParameters = window.location.search.replace('capacitor://localhost/reader?','');
        }
    }

    // deepcode ignore ReactIncorrectReturnValue: <please specify a reason of ignoring this>
    async componentDidMount() {

        console.log('ComponentDidMount');

        this.setQuerySearchParams();

        console.log('QueryString: ', this.querySearchParameters);

        console.log('REACT_APP_AWS_API', process.env.REACT_APP_AWS_API, ' web_version ', window.web_version);
        window.show_footer = false;

        this.bodyScrollLockTargetElement = this.bodyScrollLockTargetRef.current;
        //disableBodyScroll(this.bodyScrollLockTargetElement);

        console.log('bstyle', document.body.style.overflow)
        document.body.style.overflow = "hidden";

        window.addEventListener('resize', this.handleResize);
        console.log(`ComponentDidMount: ${new Date().getSeconds()} ${window.innerWidth} ${window.scrollY}`);
        console.log('QueryString: ', this.querySearchParameters);


        //const dynamicParam = this.props.match.params.dynamicParam;

        console.log('Params: ', this.props.match.params);

        const values = this.getVerse();

        console.log('Values: ', values)
        if (
            window.web_version &&
            values.normalizedPath &&
            `${window.location.pathname}${window.location.search}${window.location.hash}` !== values.normalizedPath
        ) {
            window.history.replaceState({}, '', values.normalizedPath);
        }

        const { token } = localStorage;
        

        let endpoint = `/v1/verses/details/search?search=${values.verse}`;

        if (values.noRef)
        {
            console.log('noRef found')
            window.location.href = '/';
            
            return;
        }

        const verse_ref = values.verse.match(/[^-.]+/g);
        let chapter = -1;
        let verseStart = -1;
        let verseEnd = -1;

        console.log('window history', window.history);
        console.log('verseref: ', verse_ref);
        console.log('routeHistory', this.props.history)

        const history = [];

        // if (this.props.history.length >= 2)
        //     for (let i = 0; i < this.props.history.length; i++)
        //         history.push(this.props.history.entries[i]);

        console.log('rhistory', history)
        
        if (verse_ref.length == 1)
        {
            trackCustomEvent("Book__" + verse_ref[0].trim(), { automatic: true })
            window.location.href = '/book/' + verse_ref[0];
        }
        if ((verse_ref.length == 2)||(verse_ref.length > 100)) // chapter or verse range
        {
            if (verse_ref.length == 2)
                trackCustomEvent("Chapter__" + values.verse.trim(), { automatic: true })

            console.log('Values', values)
            console.log('VerseRef', verse_ref)

            
            endpoint = values.pathPrefix + values.verse + ".json";
            chapter = verse_ref[1];
            verseStart = -1;

            this.context.loading(false);

            if (chapter == 0)
            {
                console.log('chapter', chapter, verse_ref)
                window.location.href = '/book/' + verse_ref[0];
            }
        }
        else if (verse_ref.length > 2) // exact chapter + verse or range, get whole chapter and filter later
        {
            if (verse_ref.length > 3)
            {
                trackCustomEvent("Verse__Range__" + values.verse, { automatic: true })
                console.log('Values', values)
                console.log('VerseRef', verse_ref)

                if (verse_ref[1] == verse_ref[4])
                {
                    verseEnd = verse_ref[5];
                }
                else
                    verseEnd = 0; // special character to say just add the rest of the chapter
            }
            else
                trackCustomEvent("Verse__" + verse_ref[0].trim() + "." + verse_ref[1].trim(), { automatic: true })

            endpoint = values.pathPrefix + verse_ref[0] + "." + verse_ref[1] + ".json";
            chapter = verse_ref[1];
            verseStart = verse_ref[2];


            console.log('Endpoin', endpoint)

            // In this case the endpoint looks up the whole chapter and filtering occurs later

            this.context.loading(false);

            if (verseStart == 0)
            {
                console.log('chapter', chapter, verseStart, verse_ref)
                window.location.href = '/book/' + verse_ref[0];
            }
        }
        else if (process.env.REACT_APP_AWS_API || !web_version) {
            // if (!web_version)
            // {
            //     const network = await Network.getStatus();
            //     console.log("network status: ", network)
            //     if (!network.connected)
            //     {
            //         console.log('no internet connection');
            //         this.props.alert.show('no internet connection');
            //         return;
            //     }
            // }
            this.context.loading(true);

            endpoint = `${window.API_BASE}${endpoint}`;
        }

        console.log('MyHistory', window.reading_history);
        window.reading_history.push(endpoint);
        console.log('MyHistory', window.reading_history);
        
        console.log('verse', verse_ref, values)

        console.log(`Request: ${endpoint}`, process.env.REACT_APP_AWS_API);

        fetch(endpoint, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((res) => {
                console.log('res',res);

                if (res.status > 200)
                {
                    console.log('Status issue: ' + values.verse)
                }

                return res.text()
            })
            .then((resp) => {
                if (!resp.startsWith('{'))
                    this.handleRefError(values);

                const response = JSON.parse(resp);
                console.log(`response time: ${new Date().getSeconds()}`);
                console.log('response', response);
                console.log('status', response.status);

                // const text = [];
                // let data = response.verses;
                // const sources = response.sourceinfo;
                // const subgroup = response.book.Subgroup;

                // if (verse_ref.length === 3)
                // {
                //     data = [response.verses[verse_ref[2] - 1]];
                //     console.log('data', data)
                //     if ((!data) || (data.length === 0) || !data[0])
                //         this.handleRefError(values);
                // }

                // let title = '';
                // let book = '';

                // if (data.length > 0)
                // {
                //     for (let i = 0; i < data.length; i += 1) {
                //         const verseinfo = data[i];

                //         text.push({
                //             row: (i + 1),
                //             text: verseinfo.verse,
                //             data: verseinfo,
                //         });

                //         if (title === '') { title = `${data[i].what.name} ${data[i].what.chapter}`; }
                //         if (book === '') { book = `${data[i].what.osisRef.split('.')[0]}`; }
                //     }
                // }
                // else
                // {
                //     book = response.book.BookCode;
                //     title = response.book.Name;
                // }

                // this.setState({
                //     loading: false,
                //     text,
                //     title,
                //     sources,
                //     book,
                //     colorIndex: bookConverters.convertSubGroupToIndex(subgroup),
                // });

                const stateUpdate = BibleReader.processAPIData(response, chapter, verseStart, verseEnd);
                stateUpdate.chapterIntro = buildChapterIntro(response, values.canonicalVerse || values.verse);
                stateUpdate.bookMeta = response.book || {};
                this.setState(stateUpdate);

                this.context.loading(false);
            }).catch((e)=>{
                console.log('exception', e);
                const bookName = bcv.parse(values.verse).osis();
                console.log('bookName',bookName);
                this.context.loading(false);
                this.props.alert.show('could not connect to server')
            });

            console.log('final');
    }

    getVerse()
    {
        console.log('getVerse', window.location.pathname, this.querySearchParameters)
        if (window.location.pathname.includes('/reader'))
            return { ...querystring.parse(this.querySearchParameters.substr(1)), pathPrefix: '/data/' }
        else
        {
            const pathVerse = window.location.pathname
                .replace('/read/','')
                .replace(/\/+/g, '/')
                .replace(/\/+$/g, '')
                .replace(/\.html$/g, '');
            const searchParams = new URLSearchParams(this.querySearchParameters);
            const hashFocusVerse = osisConverters.sanitizeOsisRange(
                decodeURIComponent((window.location.hash || '').replace(/^#/, '')),
            );
            const focusVerse = osisConverters.sanitizeOsisRange(searchParams.get('focus')) || hashFocusVerse;
            const selectedVerse = focusVerse || pathVerse;
            const canonicalVerse = osisConverters.toChapterReference(pathVerse || selectedVerse);
            searchParams.delete('focus');
            const normalizedQuery = searchParams.toString();
            const normalizedHash = selectedVerse && selectedVerse !== canonicalVerse
                ? `#${encodeURIComponent(selectedVerse)}`
                : '';
            const normalizedPath = `/read/${canonicalVerse}/${normalizedQuery ? `?${normalizedQuery}` : ''}${normalizedHash}`;

            return {
                verse: selectedVerse,
                pathVerse,
                canonicalVerse,
                focusVerse,
                normalizedPath,
                pathPrefix: '/data/'
            }
        }
    }

    getDevotional()
    {
        console.log({location: this.querySearchParameters});
        const searchParams = new URLSearchParams(this.querySearchParameters);
        console.log({searchParams});
        console.log({dev:searchParams.get('dev')})
        return searchParams.get('dev');
    }

    getApplicationQuestions()
    {
        const searchParams = new URLSearchParams(this.querySearchParameters);
        return searchParams.get('aq');
    }

    getMeditation()
    {
        const searchParams = new URLSearchParams(this.querySearchParameters);
        return searchParams.get('m');
    }

    getComprehensionQuestions()
    {
        const searchParams = new URLSearchParams(this.querySearchParameters);
        return searchParams.get('cq');
    }

    getCommentary()
    {
        const searchParams = new URLSearchParams(this.querySearchParameters);
        return searchParams.get('c');
    }

    getApplicationAdvice()
    {
        const searchParams = new URLSearchParams(this.querySearchParameters);
        return searchParams.get('a');
    }

    getCrossReferences()
    {
        const searchParams = new URLSearchParams(this.querySearchParameters);
        return searchParams.get('cr');
    }

    async copyContent(content)
    {
        console.log('content', content);
        console.log('text', this.state.text);
        console.log('sources', this.state.sources);
        let verses = "";
        this.state.text.forEach(v => {
            verses += v.text;
        })

        const devotionalText = 
        `# Devotional: ${content.title}
**${this.state.title}**
>${verses}


**Comprehension**
*${content.commentary}*
\`\`\`


\`\`\`
**Application**
*${content.application}*
\`\`\`


\`\`\`
**Meditation**
*Take 3 minutes to consider the mediation below and note your thoughts:*
${content.meditation}
\`\`\`


\`\`\`
*BibleSource: ${this.state.sources.verse[0].permissions}*
   
*Devotionals (c) ai-bible.com 2024*
        `

        try {
            await navigator.clipboard.writeText(devotionalText);
            alert('copied to clipboard');
        } catch (error) {
            alert('could not copy to clipboard');
        }
    }

    handleRefError(values) {
        if (values.verse.split('.').length > 0)
        {
            const bookHint = values.verse.split('.')[0];

            console.log('bookHint', bookHint)

            const osisAttempt = bookHint + '.1';
            var osisRef = bcv.parse(osisAttempt).osis();

            if (osisRef)
            {
                const book = osisRef.split('.')[0];
                console.log('book', book)
                //window.location.href =`http://${window.location.host}/book?ref=${book}`;
            }
            else  window.location.href =`http://${window.location.host}/?noRef=${values.verse}`;
        }
        else
            window.location.href =`http://${window.location.host}/?noRef=${values.verse}`;
    }
    
    trimY = this.sourcePermissionsRef?.current?.getBoundingClientRect().y;

    render() {
        //this.trimY = this.sourcePermissionsRef?.current?.getBoundingClientRect().y;
        //console.log('Trimy', this.trimY);
        this.setQuerySearchParams();

        console.log('Decoding url: ', this.querySearchParameters)
        const verseValues = this.getVerse();
        const searchedVerse = verseValues.verse
        let _meditation = decodeQueryValue(this.getMeditation());
        let _commentary = decodeQueryValue(this.getCommentary());
        let _application = decodeQueryValue(this.getApplicationAdvice());
        let _devotional = parseJsonQueryValue(this.getDevotional());
        let _applicationQuestions = parseJsonQueryValue(this.getApplicationQuestions());
        let _comprehensionQuestions = parseJsonQueryValue(this.getComprehensionQuestions());
        let _crossReferences = decodeQueryValue(this.getCrossReferences());
        let _title = "";
        let _content = {};

        if (!_devotional) {
            if (_comprehensionQuestions?.[0]) _commentary = `${_commentary || ''}\n\n${_comprehensionQuestions[0]}`;
            if (_applicationQuestions?.[0]) _application = `${_application || ''}\n\n${_applicationQuestions[0]}`;
        }

        if (_devotional == 'null') _devotional = null;
        if (_commentary == 'null') _commentary = null;
        if (_applicationQuestions == 'null') _applicationQuestions = null; 
        if (_crossReferences == 'null') _crossReferences = null; 

        console.log('Checking if it is devotional: ', _devotional)
        if (_devotional)
        {
            console.log('Loading devotional: ', _devotional)
            // let title, osisRange
            let {
                osisRange,
                title,
                commentary,
                application,
                meditation,
                crossReferences
            } = Devotionals.getDevotional(_devotional);

            console.log('chk', commentary, commentary)

            _title = title;
            _commentary = commentary;
            _application = application;
            _meditation = meditation;
            _crossReferences = crossReferences;

            _content = {
                title,
                commentary,
                application,
                meditation,
                crossReferences
            }
        }

        console.log('chk', _application, _crossReferences)

        console.log('dev', _devotional);

        let crossReferencesOutline = {};

        if (_crossReferences) {
            const cr_decoded = ((typeof(_crossReferences)==(typeof("")))?JSON.parse(_crossReferences):_crossReferences)
            const cr = cr_decoded.map(x=>{ return {osisRange:x, title:""}});
            console.log(cr, 'x', _crossReferences);
            crossReferencesOutline = books.getOutlines(cr); //.map(x => {return {subsections:[x]}});

            console.log('crossReferencesOutline', crossReferencesOutline)
        }

        console.log('query search params', this.querySearchParameters)
        console.log('search params found', [...new URLSearchParams(this.querySearchParameters).keys()])

        console.log('crossReferences', _crossReferences);


        const canonicalVerse = verseValues.canonicalVerse || osisConverters.toChapterReference(searchedVerse);
        const canonical = buildCanonicalUrl(`/read/${canonicalVerse}/`);
        console.log('canonical', canonical)
        

        const readingTitle = this.state.title || searchedVerse || 'ai-BIBLE';
        const seoTitle = buildVerseSeoTitle({
            readingTitle,
            searchedVerse,
            devotionalTitle: _title,
        });
        document.title = seoTitle;
        const aggregatedSources = this.state.sources;
        //console.log(`Sources: ${new Date().getSeconds()}`, aggregatedSources);
        const isNotFullChapter = (searchedVerse.split('.').length > 2) || searchedVerse.split('-').length > 1;

        console.log('text', this.state.text);

        const verseText = (this.state.text || []).map((entry) => entry.text).join(' ');
        const description = buildVerseSeoDescription({
            readingTitle,
            searchedVerse,
            devotionalTitle: _title,
            commentary: _commentary,
            verseText,
            chapterIntro: this.state.chapterIntro,
            subgroup: this.state.bookMeta?.Subgroup || this.state.bookMeta?.Group,
        });

        const values = verseValues;
        let filter = [];

        if (values.verse)
        {
            // based on verse range need to filter to juspt the required verses
            // set isNotFullChapter
        }

        //console.log('isNotFullChapter', isNotFullChapter, searchedVerse, searchedVerse.split('.').length)

        return (
            <>
                <SEO 
                    canonical = {canonical}
                    title = { seoTitle }
                    description = { description || ''}
                    type = 'article'
                />
                <SwipeableViews ref={this.bodyScrollLockTargetRef}>
                        
                        <div>
                            <div style={{
                                margin: 0,
                                // maxWidth: window.innerWidth,
                            }}
                            >
                                <LargeGutter></LargeGutter>
                                {(_commentary!=null)||(_applicationQuestions!=null)?<>
                                    <div className="devotional-reading-label">
                                        <h1 style={{fontFamily:'Courier', fontSize: '1em', color:'#0009', paddingTop:4, paddingBottom:4, backgroundColor:'#8882'}}>ai-bible devotional{(_title!="")?`: ${_title}`:""}</h1>
                                    </div>
                                </>:null}
                                <TitleLine
                                    title={this.state.title}
                                    book={this.state.book}
                                    visible={!this.state.loading}
                                    colorIndex={this.state.colorIndex}
                                    onclickBack={BibleReading.decrementPage}
                                    onclickFull={BibleReading.expandVerse}
                                    onclickForward={BibleReading.incrementPage}
                                    onclickCopy={()=>{this.copyContent(_content)}}
                                    isNotFullChapter={isNotFullChapter}
                                    copyContent={_devotional != null}
                                    hideBot
                                />

                                <ScrollArea trimY={0}>
                                    {this.state.chapterIntro && !_commentary ? (
                                        <div style={{paddingLeft:12, paddingRight:12, paddingTop:6}}>
                                            <CommentNotes style={{display:'block'}}>
                                                {this.state.chapterIntro}
                                            </CommentNotes>
                                            <LargeGutter></LargeGutter>
                                        </div>
                                    ) : null}
                                    {(!this.state.chapterIntro || _commentary) ? (
                                        <div className="reader-title-text-gap" style={{ minHeight: '1.4em' }} aria-hidden="true" />
                                    ) : null}
                                    <BibleReader text={this.state.text} sources={this.state.sources} colorIndex={this.state.colorIndex}></BibleReader>
                                    {(_commentary!=null)||(_applicationQuestions!=null)?
                                    <>
                                        <div>
                                            <>
                                                
                                                <h1 style={{fontFamily:'Courier', fontSize: '1em', color:'#0009', paddingLeft:20, paddingTop:4, paddingBottom:4, backgroundColor:'#8882'}}>questions</h1>
                
                                            </>    
                                        </div>

                                        <div style={{paddingLeft:16}}>
                                            <CommentHeader>Comprehension</CommentHeader>
                                            <CommentNotes>Consider the passage and comments to help answer the question below:</CommentNotes>
                                            <CommentText style={{color:'grey', whiteSpace: 'pre-line'}}>{_commentary}</CommentText>

                                            <LargeGutter></LargeGutter>
                                            <CommentNotes>Does your answer change when considering other bible verses:</CommentNotes>
                                            
                                            {
                                                <VerseOutline title="Cross References" data={crossReferencesOutline} colorIndex={this.state.colorIndex}>

                                                </VerseOutline>
                                                // JSON.parse(crossReferences).map(x=>{return (
                                                //     <>
                                                        
                                                //     </>
                                                // )})
                                            }
                                        </div>

                                        <LargeGutter></LargeGutter>
                                        <LargeGutter></LargeGutter>

                                        {_application!=null?
                                        <div style={{paddingLeft:16}}>
                                            <CommentHeader>Application</CommentHeader>
                                            {/* <CommentNotes>Use this moment to pray over what you have read:  </CommentNotes> */}
                                            <CommentNotes style={{color:'grey'}}>How can we apply what we are learning here into our lives today?</CommentNotes>
                                            <CommentText style={{color:'grey', whiteSpace: 'pre-line'}}>{_application}</CommentText>
                                        </div>:null}

                                        <LargeGutter></LargeGutter>
                                        <LargeGutter></LargeGutter>

                                        {_meditation!=null?
                                        <div style={{paddingLeft:16}}>
                                            <CommentHeader>Meditation:</CommentHeader>
                                            <CommentNotes>Still your mind and read the passage as if it were written as a letter to you. Pray and meditate in communion with God, perhaps considering the suggestion below:</CommentNotes>
                                            <CommentText style={{color:'grey', whiteSpace: 'pre-line'}}>{_meditation}</CommentText>
                                        </div>:null}
                                    </>:null}



                                </ScrollArea>
                            </div>
                        </div>
                </SwipeableViews>
            </>
        );
    }
}

export default withAlert()(withRouter(BibleReading));
